import { useEffect, useRef, useState } from "react";
import { useUISlice } from "@/models/useUISlice";
import Input from "@/components/Input";
import ModalContainer from "@/components/ModalContainer";

const AlertPrompt = () => {
  const prompt = useUISlice((state) => state.alertPrompt);
  const setPrompt = useUISlice((state) => state.setAlertPrompt);
  const [text, setText] = useState("");
  const width = useUISlice((state) => state.device.width);

  // Seed the input only when the prompt OPENS. Callers patch the payload
  // while it is up (e.g. flipping the toggle), and re-seeding on every
  // payload identity change would wipe what the user has typed.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const isOpen = Boolean(prompt);
    if (isOpen && !wasOpenRef.current) setText(prompt?.defaultValue || "");
    wasOpenRef.current = isOpen;
  }, [prompt]);

  if (!prompt) return null;

  const _onPress = () => {
    prompt.button.onPress(text);
  };

  return (
    <ModalContainer
      title={prompt.title}
      subtitle={prompt.subtitle}
      onDismiss={() => setPrompt(undefined)}
      primaryButtonText={prompt.button.text}
      onPrimaryButtonPress={_onPress}
      hideDismissIcon={prompt.hideDismissIcon}
      hideCancel={prompt.hideCancel}
      shouldPersistBackdropClick={prompt.shouldPersistBackdropClick}
      loading={prompt.loading}
      destructive={prompt.button.destructive}
      modalWidth={prompt.multiLine ? Math.min(width - 32, 500) : 360}
    >
      <div style={{ height: 16 }} />
      <Input
        autoFocus
        disabled={prompt.loading}
        height={prompt.multiLine ? "220px" : undefined}
        multiLine={prompt.multiLine}
        value={text}
        placeholder={prompt.placeholder}
        onTextChange={(text) => setText(text)}
      />
      {prompt.toggle && (
        <button
          type="button"
          role="switch"
          aria-checked={prompt.toggle.value}
          data-action-id="prompt.toggle"
          disabled={prompt.loading}
          onClick={() => prompt.toggle?.onChange(!prompt.toggle.value)}
          className="mt-4 w-full min-h-[var(--tap-min)] flex items-center justify-between rounded-2xl bg-[var(--fill-1)] px-4 py-3 text-start active:bg-[var(--fill-2)] transition-colors disabled:opacity-50"
        >
          <span className="text-sm font-medium text-[var(--text-primary)] pr-3">
            {prompt.toggle.label}
          </span>
          <span
            className={`shrink-0 relative w-[52px] h-8 rounded-full transition-colors duration-200 ${
              prompt.toggle.value
                ? "bg-[var(--accent)]"
                : "bg-[var(--fill-3)]"
            }`}
          >
            <span
              className={`switch-thumb absolute top-1 left-1 w-6 h-6 rounded-full shadow-[0_2px_6px_rgba(0,0,0,0.28)] transition-transform duration-200 ${
                prompt.toggle.value ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </span>
        </button>
      )}
    </ModalContainer>
  );
};

export default AlertPrompt;
